import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, Space, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ImportResult, ActionStatus, ActionEntityType, FieldType } from '@pmp/api';
import { ImportMatchingButton, ImportButton } from '@pmp/components';
import { Guard, useAuthz } from '@ohpen/react-authz';
import { FeatureFlag } from '@pmp/contexts';
import { useValueListsStore } from '@pmp/stores';
import { columns } from './tableConfig';
import { ImportsListProps } from './interfaces';
import { getActionStatusTagMappings, getFilteredEntityTypeList } from '@pmp/utils';

const rowKey: keyof ImportResult = 'importId';
const ENTITY_TYPES = [
  ActionEntityType.PRODUCTS,
  ActionEntityType.BASE_RATES,
  ActionEntityType.ERC_PROFILES,
  ActionEntityType.EXCHANGE_RATES,
  ActionEntityType.FEES,
  ActionEntityType.FEE_PROFILES,
  ActionEntityType.LENDER_FIRMS,
  ActionEntityType.EXCLUSIVITY_SETS,
];
const VALUE_LIST_NAMES: FieldType[] = [FieldType.ACTION_ENTITY_TYPE, FieldType.ACTION_STATUS];

export const ImportsList = ({
  isLoadingConfig,
  isLoadingImports,
  importsResult,
  onConfig,
  onOpenImportModal,
  onDelete,
}: ImportsListProps): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const { valueLists, getValueLists } = useValueListsStore();
  const { t } = useTranslation();
  const { envFeatureFlags } = useAuthz();
  const flags = (envFeatureFlags ?? new Set()) as Set<FeatureFlag>;

  // TODO: remove when all entity types are always active and enableFeesImport/enableFeeProfilesImport/enableFirmsImport flags are deleted
  const entityTypes = useMemo(
    () =>
      ENTITY_TYPES.filter(
        entityType =>
          (entityType !== ActionEntityType.FEES || flags.has('enableFeesImport')) &&
          (entityType !== ActionEntityType.FEE_PROFILES || flags.has('enableFeeProfilesImport')) &&
          (entityType !== ActionEntityType.LENDER_FIRMS || flags.has('enableFirmsImport')),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [envFeatureFlags],
  );

  useEffect(() => {
    getValueLists({ fetchIfMissing: VALUE_LIST_NAMES });
  }, [getValueLists]);

  const onView = useCallback(
    (record: ImportResult) => {
      setActiveRowId(record.importId);
      onOpenImportModal(record, record.importEntityType.key as ActionEntityType, true); // TODO:remove cast when API is updated
    },
    [onOpenImportModal],
  );

  const onImport = useCallback(
    (entityType: ActionEntityType) => {
      onOpenImportModal(null, entityType, false);
    },
    [onOpenImportModal],
  );

  const onEdit = useCallback(
    (record: ImportResult) => {
      setActiveRowId(record.importId);
      onOpenImportModal(record, record.importEntityType.key as ActionEntityType, false); // TODO:remove cast when API is updated
    },
    [onOpenImportModal],
  );

  const getRowActions: (record: ImportResult) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="view" title={t('global.buttons.view')} placement="top">
          <Button aria-label="View import" type="text" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['imports:update']} flags={['enableImports']}>
          <Tooltip key="edit" title={t('global.buttons.edit')} placement="top">
            <Button
              aria-label="Edit import"
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={record.status.key !== ActionStatus.DRAFT}
            />
          </Tooltip>
          <Guard key="delete" permissions={['imports:delete']} flags={['enableImports']}>
            <Tooltip key="delete" title={t('global.buttons.delete')} placement="top">
              <Button
                aria-label="Delete import"
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
                disabled={record.status.key !== ActionStatus.DRAFT}
              />
            </Tooltip>
          </Guard>
        </Guard>,
      ];
      return rowActions;
    },
    [onDelete, onEdit, onView, t],
  );

  const tableProps: TableProps<ImportResult> = useMemo(() => {
    const filteredEntitiesValueLists = {
      ...valueLists,
      [FieldType.ACTION_ENTITY_TYPE]: getFilteredEntityTypeList(valueLists, entityTypes),
    };

    return {
      ref: tableRef,
      id: 'imports',
      dataSource: importsResult ?? [],
      columns,
      rowKey,
      pagination: true,
      scroll: { x: 'max-content', y: 'table-height' },
      valueLists: filteredEntitiesValueLists,
      loading: isLoadingImports && !importsResult,
      className: 'flat-table',
      tagMappings: getActionStatusTagMappings(t),
      activeRowId,
      getRowActions,
    };
  }, [valueLists, importsResult, isLoadingImports, activeRowId, getRowActions, entityTypes, t]);

  return (
    <Table {...tableProps}>
      <Table.ActionsBar>
        <Flex justify="space-between">
          <Space size="middle">
            <span />
          </Space>
          <Space size="middle">
            <ImportMatchingButton onConfig={onConfig} entityTypes={entityTypes} loading={isLoadingConfig} />
            <Guard permissions={['imports:create']} flags={['enableImports']}>
              <ImportButton onImport={onImport} entityTypes={entityTypes} />
            </Guard>
          </Space>
        </Flex>
      </Table.ActionsBar>
    </Table>
  );
};
